/** CER attivi per movimenti/FIR — solo elenco preferiti . */
import { useQuery } from "@tanstack/react-query";
import { ApiError, endpoints } from "@/api/client";
import {
  getDefaultCer,
  listCerCatalog,
  type CerCatalogEntry,
} from "@/lib/cerCatalog";
import { formatEerDisplay } from "@/lib/movimento";
import type { MovimentoFormState } from "@/lib/movimentoForm";
import type { RentriChoice } from "@/lib/rentriChoices";

function apiToLocal(
  rows: Awaited<ReturnType<typeof endpoints.cerCatalog.list>>,
): CerCatalogEntry[] {
  return rows.map((r) => ({
    id: r.id,
    codice: r.codice_eer,
    descrizione: r.descrizione ?? "",
    stato_fisico: r.stato_fisico ?? "",
    provenienza: (r.provenienza === "U" ? "U" : "S") as "U" | "S",
    scadenza_analisi: r.scadenza_analisi ?? undefined,
    predefinito: r.is_default,
    created_at: new Date().toISOString(),
  }));
}

export async function fetchCerAttiviItems(): Promise<{
  source: "api" | "local";
  items: CerCatalogEntry[];
}> {
  try {
    const rows = await endpoints.cerCatalog.list();
    if (!Array.isArray(rows)) {
      throw new Error("Risposta cer-catalog non valida");
    }
    return { source: "api", items: apiToLocal(rows) };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status >= 500)) {
      return { source: "local", items: listCerCatalog() };
    }
    throw e;
  }
}

export function useCerAttiviList() {
  return useQuery({
    queryKey: ["cer-catalog"],
    queryFn: fetchCerAttiviItems,
    refetchOnMount: "always",
  });
}

export function cerEntryToChoice(entry: CerCatalogEntry): RentriChoice {
  const codice = entry.codice.replace(/\./g, "");
  return {
    value: codice,
    label: formatEerDisplay(entry.codice),
    description: entry.descrizione || undefined,
  };
}

export function cerEntriesToChoices(items: CerCatalogEntry[]): RentriChoice[] {
  return items.map(cerEntryToChoice);
}

export function findCerEntry(
  items: CerCatalogEntry[],
  eer: string,
): CerCatalogEntry | undefined {
  const norm = eer.replace(/\./g, "").trim();
  return items.find((e) => e.codice.replace(/\./g, "") === norm);
}

export function applyCerToFirForm<T extends { eer: string; stato_fisico: string; provenienza: string }>(
  form: T,
  entry: CerCatalogEntry,
): T {
  return {
    ...form,
    eer: entry.codice.replace(/\./g, ""),
    stato_fisico: entry.stato_fisico,
    provenienza: entry.provenienza,
  };
}

export function applyCerToMovimentoForm(
  form: MovimentoFormState,
  entry: CerCatalogEntry,
): MovimentoFormState {
  return {
    ...form,
    eer: entry.codice.replace(/\./g, ""),
    descrizione_eer: entry.descrizione,
    provenienza: entry.provenienza,
    stato_fisico: entry.stato_fisico,
  };
}

export function defaultCerEntry(items: CerCatalogEntry[]): CerCatalogEntry | undefined {
  return items.find((i) => i.predefinito) ?? items[0] ?? getDefaultCer();
}
